import React, { FC } from 'react';
import { createUseStyles } from 'react-jss';
import { Button, Icon } from '@packages/ui/shared';
import { getSubaruDiagnosticsUrl } from '@web/utils/can-view-stis';
import { useStisSubscriptionExpiry } from '@web/hooks/use-stis-subscription-expiry';

const useStyles = createUseStyles({
    '@keyframes slideDown': {
        from: { opacity: 0, transform: 'translateY(-6px)' },
        to: { opacity: 1, transform: 'translateY(0)' },
    },
    wrapper: {
        animation: '$slideDown 0.25s ease-out',
        marginBottom: 24,
    },
    banner: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: '#FEF9E7',
        border: '1px solid #F5C518',
        borderRadius: 8,
        padding: '16px 24px',
        gap: 16,
        '@media (max-width: 768px)': {
            flexDirection: 'column',
            alignItems: 'flex-start',
        },
    },
    contentWrapper: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        flex: 1,
        minWidth: 0,
    },
    iconWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        marginTop: 2,
    },
    textWrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        minWidth: 0,
    },
    title: {
        color: '#071424',
        fontFamily: 'Roboto',
        fontSize: 15,
        fontWeight: 600,
        lineHeight: '160%',
    },
    description: {
        color: '#4C525D',
        fontFamily: 'Roboto',
        fontSize: 14,
        fontWeight: 400,
        lineHeight: '160%',
    },
    actionsWrapper: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexShrink: 0,
        '@media (max-width: 768px)': {
            width: '100%',
            justifyContent: 'flex-end',
        },
    },
    dismissButton: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '4px 6px',
        borderRadius: 4,
        display: 'flex',
        alignItems: 'center',
        color: '#59616c',
        transition: 'background-color 0.15s',
        '&:hover': {
            backgroundColor: '#feecc0',
        },
    },
});

const formatDate = (date: Date): string =>
    date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

const getExpiryLabel = (days: number): string => {
    if (days === 0) return 'expires today';
    if (days === 1) return 'expires tomorrow';
    return `expires in ${days} days`;
};

export const StisSubscriptionExpiryBanner: FC = () => {
    const classes = useStyles();
    const expiry = useStisSubscriptionExpiry();

    if (!expiry || expiry.isDismissed) return null;

    const { expiresAt, daysUntilExpiry, dismiss } = expiry;

    return (
        <div className={classes.wrapper} role="alert" aria-live="polite">
            <div className={classes.banner}>
                <div className={classes.contentWrapper}>
                    <div className={classes.iconWrapper}>
                        <Icon name="warning" size={22} color="yellowOne" />
                    </div>
                    <div className={classes.textWrapper}>
                        <span className={classes.title}>Your STIS subscription {getExpiryLabel(daysUntilExpiry)}</span>
                        <span className={classes.description}>
                            Expiration date: <strong>{formatDate(expiresAt)}</strong>. Renew at SubaruDiagnostics.com to
                            avoid losing access to STIS content.
                        </span>
                    </div>
                </div>

                <div className={classes.actionsWrapper}>
                    <Button
                        variant="primary"
                        onPress={() => window.open(getSubaruDiagnosticsUrl(), '_blank', 'noopener,noreferrer')}
                        iconRight={<Icon name="chevron-right" size={14} color="white" />}
                    >
                        Renew subscription
                    </Button>
                    <button
                        type="button"
                        className={classes.dismissButton}
                        onClick={dismiss}
                        aria-label="Dismiss subscription expiry notification"
                        title="Dismiss"
                    >
                        <Icon name="x-close" size={18} color="graySix" />
                    </button>
                </div>
            </div>
        </div>
    );
};
